import { Fragment, useMemo } from "react";
import moment from "moment";

import { MandateTable, MandateTableData } from "./MandateTable";
import { api } from "../../client";
import { ApiPaths } from "@/providers/smarta-banking/banking-api-client";
import { ConnectionTable, ConnectionTableData } from "./ConnectionTable";
import { Spacer, Table } from "../../../components/styles";

type Props = {
  customerId: string;
};

export const ConnectionsOverview = ({ customerId }: Props) => {
  const { data: mandates } = api.useQuery(
    "get",
    ApiPaths.getApiMandatesByCustomerId,
    {
      params: {
        path: {
          customerId,
        },
      },
    }
  );

  const { data: connections } = api.useQuery(
    "get",
    ApiPaths.getApiCustomersByCustomerIdConnections,
    {
      params: { path: { customerId } },
    }
  );

  const { data: accounts } = api.useQuery(
    "get",
    ApiPaths.getApiCustomersByCustomerIdAccounts,
    {
      params: { path: { customerId } },
    }
  );

  // Prepare mandates (exclude activated)
  const mandatesData = useMemo<MandateTableData[]>(
    () =>
      (mandates?.data || [])
        .filter((m) => m.state !== "activated")
        .map((m) => ({ ...m, type: "mandate" })),
    [mandates?.data]
  );

  // Prepare connections
  const connectionsData = useMemo<ConnectionTableData[]>(
    () =>
      (connections?.data || []).map((c) => ({
        ...c,
        type: "connection",
        accounts: accounts?.data?.filter((a) => a.connectionId === c.id) || [],
      })),
    [connections?.data, accounts?.data]
  );

  // Merge and sort by updatedAt desc
  const data = useMemo<Array<MandateTableData | ConnectionTableData>>(
    () =>
      [...mandatesData, ...connectionsData].sort(
        (a, b) => moment(b.updatedAt).valueOf() - moment(a.updatedAt).valueOf()
      ),
    [mandatesData, connectionsData]
  );

  return (
    <Table>
      {data.map((connectionOrMandate) => {
        return (
          <Fragment
            key={
              connectionOrMandate.type === "mandate"
                ? `mandate-${connectionOrMandate.reference}`
                : `connection-${connectionOrMandate.id}`
            }
          >
            {connectionOrMandate.type === "mandate" && (
              <MandateTable data={connectionOrMandate} />
            )}
            {connectionOrMandate.type === "connection" && (
              <ConnectionTable
                customerId={customerId}
                data={connectionOrMandate}
              />
            )}
            <Spacer>
              <tr>
                <td>&nbsp;</td>
              </tr>
            </Spacer>
          </Fragment>
        );
      })}
    </Table>
  );
};
